/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { IoMdAddCircle } from 'react-icons/io';
import AppButton from '@/components/atoms/AppButton';
import AppToggle from '@/components/atoms/AppToggle';
import { DataTable } from '@/components/organism/Table';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import { SmartCoolerProductsExpandedPanel } from '@/components/organism/TableExpand/SmartCoolerProductsExpanded';
import PageToolbar from '@/components/organism/Cards/PageToolbar';

import GlobalSearch from '@/components/atoms/GlobalSearch';
import Autocomplete from '@/components/elements/AutoComplete';
import DownloadReport from '@/components/organism/ExportDataModal/DownloadReport';
import ConfirmActionModal, {
  DeleteIcon,
  ActivateIcon,
} from '@/components/organism/ConfirmActionModal';

import { useDebounce } from '@/hooks/useDebounce';
import { getSelectedTimeZone } from '@/utils/dateTime';
import { trimSpacesInPayload } from '@/utils/stringFormatting';
import useDeepEffect from '@/hooks/useDeepEffect';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import { useScreenClickDetector } from '@/hooks/useScreenClickDetector';
import { useExportFormats } from '@/hooks/useExportFormats';
import { useDateFilter } from '@/context/dateFilter.context';

import {
  fetchSmartCoolerProducts,
  fetchSmartCoolerProductById,
  clearSmartCoolerProduct,
  deleteSmartCoolerProduct,
  deleteSmartCoolerProductFailure,
  activateSmartCoolerProduct,
  setShouldRefetchSmartProducts,
  activateSmartCoolerProductFailure,
} from '@/store/slices/smartCoolerProducts.slice';
import { getSmartCoolerProductsState } from '@/store/selectors/smartCoolerProducts.selector';
import { subApplicationList } from '@/store/selectors/subApplication.selector';
import { getSubApplications } from '@/store/slices/subApplication.slice';

import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { OPERATOR_PROPS } from '@/constant/operator/operator.constant';
import {
  pagination,
  STRINGS,
  ALL_SUB_APPLICATION,
  COMMON_VALIDATIONS,
  ROW_PER_PAGE_OPTION,
  REPORT_FILE_NAMES,
  MODAL_TITLES,
} from '@/constant/global/global.constant';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import { ERequestMethods } from '@/constant/downloadReport/downloadReport';
import {
  EAllowedFieldsForExport,
  EExportPage,
} from '@/components/organism/ExportDataModal/export.types';
import { getRole, ROLES } from '@/constant/users/user.constant';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/smartCoolerProducts.testIds';
import { buildColumns } from './smartCoolerProducts.columns';
import {
  CONFIRMATION_MODALS,
  SMART_COOLER_PRODUCTS_PAGE_RESPONSE_MESSAGES,
  SMART_COOLER_PAGE_PROPS,
} from './constant';
import CrudCoolerProductModal from './CrudCoolerProductModal';

const SmartCoolerProducts = () => {
  const [isCopy, setIsCopy] = useState('');
  const hasLoaded = useRef(false);
  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;
  const [isDeleted, setIsDeleted] = useState(false);
  const [searchByProductName, onSearchByProductName] = useState('');
  const [barcode, onSearchByBarcode] = useState('');
  const [productIdIdentifier, setProductIdIdentifier] = useState('');
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: 'desc',
  });
  const [selectedSubApplication, setSelectedSubApplication] =
    useState(ALL_SUB_APPLICATION);
  const [scpPagination, setScpPagination] = useState({
    limit: ROW_PER_PAGE_OPTION[0],
    page: pagination.page,
  });
  const [openCreateSCPDialog, setOpenCreateSCPDialog] = useState(false);
  const [openEditSCPDialog, setOpenEditSCPDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState({});
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [isMsgOpen, setIsMsgOpen] = useState({});
  const exportFormats = useExportFormats(EExportPage.SC);
  const selectedTimezone = getSelectedTimeZone();

  const { reset } = useForm({
    mode: 'onChange',
    defaultValues: { deleteConfirm: '', activateConfirm: '' },
  });

  const dispatch = useDispatch();
  const toast = useToast();
  const subApplicationListData = useSelector(subApplicationList);
  const {
    smartCoolerProducts: smartCoolerProductsData,
    loadingProducts,
    message,
    shouldRefetchSmartCoolerProducts,
  } = useSelector(getSmartCoolerProductsState);

  const data = smartCoolerProductsData?.data ?? [];
  const paginationResponse = smartCoolerProductsData?.pagination ?? {
    totalRecords: 0,
  };
  const totalPages = Math.ceil(
    (paginationResponse?.totalRecords ?? 0) / scpPagination.limit,
  );

  const barcodeQuery = useDebounce(
    barcode,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const productIdIdentifierQuery = useDebounce(
    productIdIdentifier,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const searchByProductNameQuery = useDebounce(
    searchByProductName,
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const toggleMsgOpen = useCallback((id) => {
    setIsMsgOpen((prev) => {
      const r = Object.keys(prev).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {});
      return { ...r, [id]: !prev[id] };
    });
  }, []);

  const expandedRowIndex = useMemo(() => {
    if (!data.length) return null;
    const idx = data.findIndex(
      (row) => isMsgOpen[row.smartCoolerProductId] === true,
    );
    return idx >= 0 ? idx : null;
  }, [data, isMsgOpen]);

  const handlePageSizeChange = useCallback(
    (value) => {
      setScpPagination((prev) => ({ ...prev, limit: value }));
      if (totalPages < scpPagination.page)
        setScpPagination((prev) => ({ ...prev, page: totalPages }));
    },
    [totalPages, scpPagination.page],
  );

  const handlePreviousClick = useCallback(
    (page) => {
      if (page > 1 && page <= totalPages)
        setScpPagination((prev) => ({ ...prev, page: page - 1 }));
    },
    [totalPages],
  );

  const handleNextClick = useCallback(
    (page) => {
      if (page >= 1 && page < totalPages)
        setScpPagination((prev) => ({ ...prev, page: page + 1 }));
    },
    [totalPages],
  );

  const getSmartCoolerProductParams = useCallback(() => {
    const formatted = trimSpacesInPayload({
      ...(barcodeQuery?.length > 0 ? { barcode: barcodeQuery } : {}),
      ...(productIdIdentifierQuery?.length > 0
        ? { productIdIdentifier: productIdIdentifierQuery }
        : {}),
      ...(searchByProductNameQuery?.length > 0
        ? { name: searchByProductNameQuery }
        : {}),
    });
    const orderBy =
      sorting.orderBy === 'shortProductName' ? 'name' : sorting.orderBy;
    return {
      ...scpPagination,
      ...sorting,
      orderBy,
      ...formatted,
      subAppId: selectedSubApplication?.id,
      createdStartDate: startDate,
      createdEndDate: endDate,
      inactive: isDeleted,
    };
  }, [
    barcodeQuery,
    productIdIdentifierQuery,
    searchByProductNameQuery,
    sorting,
    scpPagination,
    selectedSubApplication,
    startDate,
    endDate,
    isDeleted,
  ]);

  const fetchList = useCallback(
    (params) => {
      try {
        dispatch(fetchSmartCoolerProducts({ ...params }));
      } catch (err) {
        toast?.pushWarning(
          err?.response?.data?.message,
          PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
        );
      }
    },
    [dispatch, toast],
  );

  const handleDeleteProduct = async (item) => {
    try {
      dispatch(
        deleteSmartCoolerProduct({
          smartCoolerProductId: item?.smartCoolerProductId,
          transactionId: uuidv4(),
        }),
      );
      setIsDeleteOpen(false);
      reset({ deleteConfirm: '' });
      fetchList(getSmartCoolerProductParams());
    } catch (err) {
      dispatch(deleteSmartCoolerProductFailure(err?.message));
      setIsDeleteOpen(false);
    }
  };

  const handleActivateProduct = async (item) => {
    try {
      dispatch(
        activateSmartCoolerProduct({
          smartCoolerProductId: item?.smartCoolerProductId,
          transactionId: uuidv4(),
        }),
      );
      setActivateConfirmOpen(false);
      reset({ activateConfirm: '' });
      fetchList(getSmartCoolerProductParams());
    } catch (err) {
      dispatch(activateSmartCoolerProductFailure(err?.message));
      toast?.pushError(
        err?.response?.data?.message ||
        SMART_COOLER_PRODUCTS_PAGE_RESPONSE_MESSAGES.TOAST_MESSAGES.ACTIVATION
          .FAILURE,
        10000,
      );
      setActivateConfirmOpen(false);
    }
  };

  const onCopyClick = useCallback((key) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 1500);
  }, []);

  // Track first successful data load for skeleton vs refetch indicator
  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0) {
      hasLoaded.current = true;
    }
  }, [data]);

  // Page guard
  useEffect(() => {
    if (scpPagination.page > totalPages && totalPages > 0)
      setScpPagination((prev) => ({ ...prev, page: 1 }));
  }, [totalPages, scpPagination.page]);

  useDeepEffect(
    () => {
      if (
        (barcodeQuery?.length > 0 && barcodeQuery?.length < 3) ||
        (productIdIdentifierQuery?.length > 0 &&
          productIdIdentifierQuery?.length < 3) ||
        (searchByProductNameQuery?.length > 0 &&
          searchByProductNameQuery?.length < 3)
      ) {
        toast?.pushWarning(
          COMMON_VALIDATIONS.INPUT_LENGTH,
          PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
        );
        return;
      }
      const params = getSmartCoolerProductParams();
      fetchList(params);
      toggleMsgOpen('');
    },
    [
      scpPagination,
      barcodeQuery,
      productIdIdentifierQuery,
      searchByProductNameQuery,
      sorting,
      isDeleted,
      selectedSubApplication?.id,
      startDate,
      endDate,
    ],
    true,
  );

  useEffect(() => {
    if (
      message ===
      SMART_COOLER_PRODUCTS_PAGE_RESPONSE_MESSAGES.TOAST_MESSAGES.ACTIVATION
        .BACKEND_RESPONSE
    ) {
      toast?.pushSuccess(
        SMART_COOLER_PRODUCTS_PAGE_RESPONSE_MESSAGES.TOAST_MESSAGES.ACTIVATION
          .SUCCESS,
        PROVIDER_CONSTANT_PROPS?.GENERIC?.TOAST_TIME,
      );
    }
  }, [message]);

  const { hasClicked, resetClickStatus } = useScreenClickDetector();
  useEffect(() => {
    return () => {
      toast?.clearAll?.();
    };
  }, []);
  useEffect(() => {
    if (hasClicked && toast) {
      toast.clearAll();
    }
    hasClicked && resetClickStatus();
  }, [hasClicked]);
  useEffect(() => {
    if (shouldRefetchSmartCoolerProducts) {
      fetchList(getSmartCoolerProductParams());
      dispatch(setShouldRefetchSmartProducts(false));
    }
  }, [shouldRefetchSmartCoolerProducts]);
  useEffect(() => {
    dispatch(getSubApplications({ limit: 100, page: 1 }));
  }, []);

  const columns = useMemo(
    () =>
      buildColumns(
        isCopy,
        onCopyClick,
        (item) => {
          setEditingProduct(item);
          setOpenEditSCPDialog(true);
        },
        (item) => {
          setEditingProduct(item);
          setIsDeleteOpen(true);
        },
        (item) => {
          setEditingProduct(item);
          setActivateConfirmOpen(true);
        },
      ),
    [isCopy],
  );

  const allowedFields = EAllowedFieldsForExport[EExportPage.SC] || [];

  return (
    <>
      <FullPageCard
        testId={tid(TID.PAGE)}
        className="rounded-b-none"
        header={
          <PageToolbar
            title={STRINGS.SMART_COOLER_PRODUCTS}
            subtitle={STRINGS.SMART_COOLER_PRODUCTS_SUB}
            icon={getNavItemIcon(NAV_ITEMS.SMART_COOLER_PRODUCTS)}
            actionsSlot={
              <div className="flex items-center gap-2">
                <GlobalSearch
                  placeholder="SCP Id / Legacy Id"
                  dataTestId={tid(TID.SEARCH_PRODUCT_ID)}
                  globalSearch={productIdIdentifier}
                  setGlobalSearch={setProductIdIdentifier}
                  isDataFound={!isEmpty(data)}
                  className="w-[150px]"
                />
                <GlobalSearch
                  placeholder="Barcode"
                  dataTestId={tid(TID.SEARCH_BARCODE)}
                  globalSearch={barcode}
                  setGlobalSearch={onSearchByBarcode}
                  isDataFound={!isEmpty(data)}
                  className="w-[130px]"
                />
                <GlobalSearch
                  placeholder="Product Name"
                  dataTestId={tid(TID.SEARCH_NAME)}
                  globalSearch={searchByProductName}
                  setGlobalSearch={onSearchByProductName}
                  isDataFound={!isEmpty(data)}
                  className="w-[140px]"
                />
              </div>
            }
            actionButtons={
              <div className="flex items-center gap-2">
                <DownloadReport
                  testId={tid(TID.EXPORT_BTN)}
                  label={EReportLabel.EXPORT_DATA}
                  type={EReportFormat.CSV}
                  url={
                    API_URL_CONFIG?.SMART_COOLER_PRODUCTS?.GET_DOWNLOAD_PDF_CSV
                  }
                  orderBy={sorting?.orderBy}
                  order={sorting?.order}
                  startDate={startDate}
                  endDate={endDate}
                  timeZone={selectedTimezone}
                  limit={paginationResponse?.totalRecords}
                  inactive={isDeleted}
                  subAppId={selectedSubApplication?.id}
                  fileName={REPORT_FILE_NAMES.SMART_COOLER_PRODUCTS_REPORT}
                  modalTitle={MODAL_TITLES.SMART_COOLER_PAGE}
                  requestMethod={ERequestMethods.POST}
                  supportingReportFormats={exportFormats}
                  selectiveFieldExport
                  allowedFieldsForExport={allowedFields}
                />
                {getRole() === ROLES.ADMIN && (
                  <AppButton
                    data-testid={tid(TID.ADD_BTN)}
                    variant="primary"
                    icon={<IoMdAddCircle className="h-4 w-4" />}
                    onClick={() => setOpenCreateSCPDialog(true)}
                  >
                    Add Product
                  </AppButton>
                )}
              </div>
            }
            filtersLeft={
              <div className="flex items-center gap-2">
                <div className="w-[200px]">
                  <Autocomplete
                    label={OPERATOR_PROPS.SUb_APPLICATION_ID.LABEL}
                    placeholder={OPERATOR_PROPS.SUb_APPLICATION_ID.PLACEHOLDER}
                    handleSelectedValue={(v) => setSelectedSubApplication(v)}
                    selectedValue={selectedSubApplication}
                    lists={subApplicationListData}
                    nameKey="name"
                    dataTestId={tid(TID.SUBAPP_SELECT)}
                    ellipsisLength={25}
                  />
                </div>
              </div>
            }
            filtersRight={
              <AppToggle
                data-testid={tid(TID.DELETED_TOGGLE)}
                checked={isDeleted}
                onChange={setIsDeleted}
                color="primary"
                size="sm"
                label={SMART_COOLER_PAGE_PROPS.SHOW_DELETED_LABEL}
                labelPosition="left"
              />
            }
          />
        }
      >
        <DataTable
          data-testid={tid(TID.TABLE)}
          data={data}
          columns={columns}
          loading={loadingProducts}
          skeletonRows={15}
          onSort={(orderBy, order) => {
            setIsMsgOpen({});
            setSorting({ orderBy, order });
          }}
          sortState={sorting}
          pagination={
            !isEmpty(data)
              ? {
                state: scpPagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handlePageSizeChange,
                onNextClick: handleNextClick,
                onPreviousClick: handlePreviousClick,
              }
              : null
          }
          renderExpandedRow={(item) => (
            <SmartCoolerProductsExpandedPanel row={item} />
          )}
          expandedRowIndex={expandedRowIndex}
          onExpandChange={(_idx, item) => {
            if (item) {
              setIsMsgOpen({ [item.smartCoolerProductId]: true });
              dispatch(fetchSmartCoolerProductById(item.smartCoolerProductId));
            } else {
              setIsMsgOpen({});
              dispatch(clearSmartCoolerProduct());
            }
          }}
          rowKey={(item, idx) => item.smartCoolerProductId ?? String(idx)}
          rowTestId={(item: any, idx: number) =>
            TID.TABLE_ROW(`${idx}`)
          }
        />
      </FullPageCard>

      {/* Create modal */}
      <CrudCoolerProductModal
        isOpen={openCreateSCPDialog}
        onClose={() => setOpenCreateSCPDialog(false)}
      />
      {/* Edit modal */}
      <CrudCoolerProductModal
        isOpen={openEditSCPDialog}
        onClose={() => setOpenEditSCPDialog(false)}
        product={editingProduct?.smartCoolerProductId ? editingProduct : null}
      />

      {/* ── Delete confirmation ── */}
      <ConfirmActionModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          reset({ [CONFIRMATION_MODALS.DELETE.REGISTER_STRING]: '' });
        }}
        onConfirm={() => handleDeleteProduct(editingProduct)}
        title={CONFIRMATION_MODALS.DELETE.TITLE}
        subtitle={SMART_COOLER_PAGE_PROPS.DELETE_MODAL_SUBTITLE}
        description={
          <>
            {CONFIRMATION_MODALS.DELETE.DESCRIPTION}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {editingProduct?.shortProductName ||
                editingProduct?.smartCoolerProductId}
            </span>
            ?
          </>
        }
        confirmWord={CONFIRMATION_MODALS.DELETE.CONFIRMATION_TEXT}
        placeholder={CONFIRMATION_MODALS.DELETE.PLACEHOLDER}
        inputLabel={SMART_COOLER_PAGE_PROPS.DELETE_INPUT_LABEL}
        hint={SMART_COOLER_PAGE_PROPS.DELETE_HINT}
        confirmLabel="Delete"
        confirmIcon={<DeleteIcon />}
        variant="danger"
        size="xl"
        icon={<DeleteIcon />}
      />

      {/* ── Activate confirmation ── */}
      <ConfirmActionModal
        isOpen={activateConfirmOpen}
        onClose={() => {
          setActivateConfirmOpen(false);
          reset({ activateConfirm: '' });
        }}
        onConfirm={() => handleActivateProduct(editingProduct)}
        title={CONFIRMATION_MODALS.ACTIVATE.TITLE}
        subtitle={SMART_COOLER_PAGE_PROPS.ACTIVATE_MODAL_SUBTITLE}
        description={
          <>
            {CONFIRMATION_MODALS.ACTIVATE.DESCRIPTION}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {editingProduct?.shortProductName ||
                editingProduct?.smartCoolerProductId}
            </span>
            ?
          </>
        }
        confirmWord={CONFIRMATION_MODALS.ACTIVATE.CONFIRMATION_TEXT}
        placeholder={CONFIRMATION_MODALS.ACTIVATE.PLACEHOLDER}
        inputLabel={SMART_COOLER_PAGE_PROPS.ACTIVATE_INPUT_LABEL}
        hint={SMART_COOLER_PAGE_PROPS.ACTIVATE_HINT}
        confirmLabel="Activate"
        confirmIcon={<ActivateIcon />}
        variant="success"
        size="xl"
        icon={<ActivateIcon />}
      />
    </>
  );
};

export default SmartCoolerProducts;
